import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Camera, CameraOff, HelpCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { EvaluationsPanel } from "./EvaluationsPanel";

export default function Roster() {
  const { classId } = useParams();
  const [roster, setRoster] = useState([]);

  const load = () => api.get(`/classes/${classId}/roster`).then(({ data }) => setRoster(data)).catch(() => {});
  useEffect(() => { load(); }, [classId]);

  const toggle = async (p) => {
    try {
      await api.post("/attendance", { class_id: classId, participant_id: p.participant_id, present: !p.present });
      setRoster((r) => r.map((x) => (x.participant_id === p.participant_id ? { ...x, present: !x.present } : x)));
    } catch { toast.error("Could not update attendance"); }
  };

  const presentCount = roster.filter((r) => r.present).length;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="roster-page">
      <Link to="/director/rosters" className="flex w-fit items-center gap-1 text-sm font-semibold text-[#920e12] mb-6"><ArrowLeft size={16} /> Back to my rosters</Link>
      <span className="block text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12]">Roster &amp; Attendance</span>
      <h1 className="mt-2 font-display text-4xl text-[#550001]">Class roster</h1>
      <p className="mt-2 text-[#545454]">{presentCount} of {roster.length} present · photo-release status shown so no one is filmed against their wishes.</p>

      <Tabs defaultValue="attendance" className="mt-8">
        <TabsList>
          <TabsTrigger value="attendance" data-testid="roster-tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="evaluations" data-testid="roster-tab-evaluations">Weekly evaluations</TabsTrigger>
        </TabsList>
        <TabsContent value="evaluations" className="mt-6"><EvaluationsPanel classId={classId} roster={roster} /></TabsContent>
        <TabsContent value="attendance">
      <div className="mt-6 space-y-3">
        {roster.map((p) => (
          <div key={p.participant_id} className="flex items-center gap-4 bg-white rounded-2xl border border-[#dfd8da] p-4" data-testid={`roster-row-${p.participant_id}`}>
            <Checkbox checked={p.present} onCheckedChange={() => toggle(p)} data-testid={`attendance-${p.participant_id}`} className="w-6 h-6" aria-label={`Mark ${p.name} present`} />
            <div className="flex-1">
              <div className="text-lg font-bold text-[#1a1617]">{p.name}</div>
              <div className="text-sm text-[#545454]">{p.present ? "Present" : "Not marked"}</div>
            </div>
            {!p.photo_release_known ? (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold bg-[#fef3c7] text-[#b45309]" data-testid={`photo-status-${p.participant_id}`}>
                <HelpCircle size={15} /> No record
              </span>
            ) : p.photo_release ? (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold bg-[#dcfce7] text-[#15803d]" data-testid={`photo-status-${p.participant_id}`}>
                <Camera size={15} /> Photo OK
              </span>
            ) : (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold bg-[#fee2e2] text-[#991b1b]" data-testid={`photo-status-${p.participant_id}`}>
                <CameraOff size={15} /> Do NOT film
              </span>
            )}
          </div>
        ))}
        {roster.length === 0 && <p className="text-[#545454]">No participants enrolled in this class yet.</p>}
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
